import React from 'react';
import { Mail, Phone, Star, Trash2, Pencil, UserPlus, KeyRound } from 'lucide-react';

const ROLE_LABELS = {
  principal: 'Principal',
  assistant_principal: 'Assistant principal',
  parent_coordinator: 'Parent coordinator',
  purchase_secretary: 'Purchase secretary',
  it_staff: 'IT staff',
  superintendent: 'Superintendent',
  other_school: 'Other (school)',
  owner: 'Owner',
  executive: 'Executive',
  technical_lead: 'Technical lead',
  billing: 'Billing',
  other_external: 'Other (external)',
};

// hasLogin: this contact's email already belongs to a portal user, so the
// "Add as user" button gives way to a small "Has login" tag.
export default function ContactCard({ contact, onEdit, onDelete, onAddUser, hasLogin = false }) {
  if (!contact) return null;
  const canAddUser = Boolean(onAddUser && contact.email && !hasLogin);
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 flex items-start gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm text-white font-medium">{contact.name}</div>
          {contact.isPrimary && (
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300 uppercase tracking-wider">
              <Star className="w-2.5 h-2.5" /> Primary
            </span>
          )}
          {contact.isDecisionMaker && (
            <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-[#B0FC38]/15 border border-[#B0FC38]/25 text-[#B0FC38] uppercase tracking-wider">
              Decision maker
            </span>
          )}
          {hasLogin && (
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-white/[0.05] border border-white/[0.08] text-gray-400 uppercase tracking-wider" title="This email already has a portal login" data-testid="contact-has-login">
              <KeyRound className="w-2.5 h-2.5" aria-hidden="true" /> Has login
            </span>
          )}
        </div>
        <div className="text-[12px] text-gray-500 mt-0.5">{contact.roleTitle || ROLE_LABELS[contact.role] || contact.role}</div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[12px] text-gray-400">
          {contact.email && (
            <a href={`mailto:${contact.email}`} className="inline-flex items-center gap-1.5 hover:text-white">
              <Mail className="w-3 h-3" /> {contact.email}
            </a>
          )}
          {contact.phone && (
            <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-1.5 hover:text-white">
              <Phone className="w-3 h-3" /> {contact.phone}
            </a>
          )}
          {contact.preferredChannel && (
            <span className="text-[11px] text-gray-600">Prefers {contact.preferredChannel.replace('_', ' ')}</span>
          )}
        </div>
        {contact.notes && <div className="text-[12px] text-gray-500 mt-2 leading-relaxed">{contact.notes}</div>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {canAddUser && (
          <button
            type="button"
            onClick={() => onAddUser(contact)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-gray-400 hover:text-white hover:bg-white/[0.05]"
            aria-label={`Add ${contact.name} as a portal user`}
            title="Create a portal login for this contact (they get an invite link)"
            data-testid="contact-add-user"
          >
            <UserPlus className="w-3.5 h-3.5" aria-hidden="true" /> Add as user
          </button>
        )}
        {onEdit && (
          <button
            onClick={() => onEdit(contact)}
            className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05]"
            aria-label="Edit contact"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
        {onDelete && (
          <button
            onClick={() => onDelete(contact)}
            className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10"
            aria-label="Delete contact"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
